import {useTaggingLoad} from "@/hooks/common/useTagging";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import Breadcrumb from "@COMPONENTS/layout/breadcrumbs/desktop";
import Footer from "@COMPONENTS/layout/footer/Desktop";
import Header from "@COMPONENTS/layout/header/Desktop";
import TaggingGTM from "@COMPONENTS/product/pages/localList/common/GTMTags";
import useFilterStore from "@COMPONENTS/product/pages/localList/common/filterHook";
import {LocalHeadArea, LocalLvl4List} from "@COMPONENTS/product/pages/localList/desktop/filter/LocalHeadFilter";
import LocalSideFilter from "@COMPONENTS/product/pages/localList/desktop/filter/LocalSideFilter";
import LocalGoodsList from "@COMPONENTS/product/pages/localList/desktop/goods/LocalGoodsList";
import * as style from "@COMPONENTS/style/productlist/productlist";
import {FloatingContent, FloatingWrap} from "@FRAMEWORK/ui/desktop/filter";
import {Container} from "@FRAMEWORK/ui/desktop/layout";
import {Title} from "@FRAMEWORK/ui/desktop/text";
import getSEO from "@FRAMEWORK/utils/seo";
import useGlobalGoodsFlag from "@QUERIES/bff/global/useGlobalGoodsFlag";
import useGoodsFilter from "@QUERIES/prdt/goods/useGoodsFilter";
import {useAppDispatch, useAppSelector} from "@STORE/index";
import {initGoodsFilter, setGoodsFilter} from "@STORE/store-filter";
import {useRouter} from "next/router";
import {useEffect, useRef, useState} from "react";

const Page = () => {
	const router = useRouter();
	const dspSid = router.query?.dspSid as string;

	// goodsFlag 미리 호출 (FlagPromotion 에서 사용)
	useGlobalGoodsFlag();

	const {getFilter, getFilteringProduct} = useFilterStore();

	const filterFetch = useGoodsFilter(dspSid);
	const filter = filterFetch.data?.body;

	const productList = getFilteringProduct();

	/* filterStore 초기화 */
	const filterStore = useAppSelector(state => state.goodsFilterStore);
	const dispatch = useAppDispatch();

	useEffect(() => {
		const storeDspSid = filterStore.goodsFilter ? Object.keys(filterStore?.goodsFilter)[0] : {};
		if (dspSid && dspSid !== storeDspSid) {
			const storeInit = {[dspSid]: [{text: "추천순", value: "sortOrder", key: "sort", asc: true}]};
			dispatch(initGoodsFilter()); /* 데이터 초기화*/
			dispatch(setGoodsFilter(storeInit)); /* dspSid 세팅 */
		}
	}, [router]);

	// 필터 영역 로우 체크
	const filteredCount = getFilter()?.filter(v => v.key !== "sort" && v.key !== "dspSid").length;

	// header 높이값
	const fixedTitle = useRef(null); // 타이틀 header
	const fixedHeader = useRef(null); // 필터 header
	const [fixedHeaderHeight, setFixedHeaderHeight] = useState(null);

	useEffect(() => {
		// fixed header 크기 조절 감지
		const fixedHeaderElement = fixedHeader?.current;
		const fixedTitleHeader = fixedTitle?.current;

		if (!fixedHeaderElement) return;
		const observer = new ResizeObserver(() => {
			setFixedHeaderHeight(fixedHeaderElement.clientHeight + fixedTitleHeader.clientHeight);
		});
		observer.observe(fixedHeaderElement);
		return () => {
			observer.disconnect();
		};
	}, [filter, filteredCount]);

	const productCount = productList.length || 0; // 상품 갯수

	/** tagging load */
	useTaggingLoad("pkg_view_goodsCdList");

	return (
		<>
			<Header />
			<FloatingContent>
				<Container>
					<Breadcrumb />
					<style.ContentWrap>
						<style.ContentHeader ref={fixedTitle}>
							<Title>{filter && filter.dspNm}</Title>
							<p>
								상품<style.Count>{productCount}</style.Count>개
							</p>
						</style.ContentHeader>
						{/* Lvl4 */}
						{filter && <LocalLvl4List {...filter} />}

						{/* headFilter */}
						{filteredCount > 0 && (
							<style.FilterHeaderWrap ref={fixedHeader}>
								<LocalHeadArea />
							</style.FilterHeaderWrap>
						)}
						<style.Content>
							<div>
								<FloatingWrap style={{top: filteredCount > 0 ? `${fixedHeaderHeight}px` : `72px`}}>
									{/* side filter */}
									<LocalSideFilter {...filter} />
								</FloatingWrap>
							</div>
							{/* product 영역 */}
							<LocalGoodsList />
						</style.Content>
					</style.ContentWrap>
				</Container>
			</FloatingContent>
			<Footer />
			{/* <div data-tagging-load="pkg_view_goodsCdList" /> */}
			{router.query?.goodsCd && (
				<ErrorBoundary>
					<TaggingGTM menu={(router.query?.menu as string) ?? ""} goodsCd={router.query?.goodsCd as string} />
				</ErrorBoundary>
			)}
		</>
	);
};

export default Page;

export async function getServerSideProps(context) {
	try {
		const {dspSid = "", goodsCd = ""} = context.query;
		const seo = await getSEO(dspSid, "goodsCd", goodsCd);

		return {
			props: {...seo},
		};
	} catch (error) {
		return {
			props: {},
		};
	}
}
